import React, { useContext, useMemo } from "react";
import styled from "styled-components";
import intl from "react-intl-universal";
import { mdiAntenna, mdiContentDuplicate, mdiPlus, mdiTrashCanOutline } from "@mdi/js";

import antennaRest from "../../../../rest/antennaRest";
import commonUtils from "../../../../script/utils/commonUtils";
import antennaUtils from "../../../../script/utils/antennaUtils";

import { useInput } from "../../../hooks/useInput";
import { useOnChange } from "../../../hooks/useOnChange";
import { useValidation } from "../../../hooks/useValidation";
import { EntryContext } from "../../../contexts/EntryContext";

import { RadioItem } from "./RadioItem";
import { Grid } from "../../../components/Grid";
import { Button } from "../../../components/Button";
import { Tooltip } from "../../../components/Tooltip";
import { Checkbox } from "../../../components/Checkbox";
import { Accordion } from "../../../components/Accordion";
import { InputNumber } from "../../../components/InputNumber";
import { SelectSingle } from "../../../components/SelectSingle";

const HeaderStyle = styled.div`
    gap: 12px;
    display: flex;
    align-items: center;
`;

const SummaryStyle = styled.div`
    gap: 4px;
    min-width: 0;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
`;

const TitleStyle = styled.div`
    font-size: 14px;
`;

const FactsStyle = styled.div`
    display: flex;
    flex-wrap: wrap;
    font-size: 12px;
    gap: 4px 16px;
    >span {
        white-space: nowrap;
    }
    >span >span {
        margin-right: 4px;
        color: ${props => props.theme.greyDarker};
    }
`;

const CountStyle = styled.div`
    font-size: 12px;
    white-space: nowrap;
    color: ${props => props.theme.greyDarker};
`;

const ActionsStyle = styled.div`
    display: flex;
`;

const BodyStyle = styled.div`
    gap: 14px;
    display: flex;
    flex-direction: column;
    padding: 14px;
`;

// looks like components/Table, which is built for the list pages and their sticky header;
// no scroll container here, it would clip the option lists of SelectSingle
const RadiosTableStyle = styled.div`
    border-radius: 2px;
    border: 1px solid ${props => props.theme.greyLight};
    >table {
        width: 100%;
        border-spacing: 0;
        table-layout: fixed;
        border-collapse: separate;
    }
    th:first-child {
        width: 22px;
    }
    th:last-child {
        width: 60px;
    }
    th {
        font-size: 12px;
        padding: 8px 6px;
        text-align: left;
        white-space: nowrap;
        vertical-align: bottom;
        background-color: ${props => props.theme.greyLightest};
        border-bottom: 1px solid ${props => props.theme.greyLight};
    }
    td {
        padding: 5px 6px;
        vertical-align: middle;
        border-bottom: 1px solid ${props => props.theme.greyLighter};
    }
    tbody tr:hover td {
        background-color: ${props => props.theme.greyLightest};
    }
    tbody tr:last-child td {
        border-bottom: none;
    }
`;

const AddRadioStyle = styled.div`
    display: flex;
    margin-top: 6px;
`;

const EmptyStyle = styled.div`
    font-size: 12px;
    padding: 12px;
    color: ${props => props.theme.greyDarker};
`;

// Tooltip centers its content, which would shrink the select to its text
const TooltipFieldStyle = styled.div`
    width: 100%;
`;

const AntennaNameRowStyle = styled.div`
    display: flex;
    cursor: pointer;
    &:hover {
        background: ${props => props.theme.greyLightest};
    }
    >div:nth-child(1){
        min-width: 230px;
        overflow: hidden;
    }
`;

const formatNumber = value => (value === null || value === undefined ? "" : value.toFixed(2).replace(".", ","));

export const SectorItem = props => {

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;

    const { sector, expanded, minMaxDowntilt } = props;
    const sectorKey = antennaUtils.itemKey(sector);

    const enabledRadios = sector.radios.filter(radio => radio.enabled).length;
    const allEnabled = sector.radios.length > 0 && enabledRadios === sector.radios.length;

    const enabled = useInput("enabled-" + sectorKey, allEnabled);
    const operator = useInput("operator-" + sectorKey, sector.operator);
    const antennaManufacturer = useInput("antennaManufacturer-" + sectorKey, sector.antennaManufacturer);
    const antennaName = useInput("antennaName-" + sectorKey, sector.antennaName);
    const positionX = useInput("positionX-" + sectorKey, sector.positionX);
    const positionY = useInput("positionY-" + sectorKey, sector.positionY);
    const height = useInput("height-" + sectorKey, sector.height);
    const direction = useInput("direction-" + sectorKey, sector.direction);
    const mechDowntilt = useInput("mechDowntilt-" + sectorKey, sector.mechDowntilt);
    const isAntennaModelOutDated = props.antennaInfo.find(antenna => antenna.manufacturer === sector.antennaManufacturer && antenna.name === sector.antennaName && antenna.operator === sector.operator)?.outdated === true;

    const validationOptions = useMemo(() => ({ globalErrorMessage: intl.get("entry.tabs.radios") }), []);
    useValidation(value => !commonUtils.isBlank(value), [antennaManufacturer], validationOptions);
    useValidation(value => !commonUtils.isBlank(value), [antennaName], validationOptions);
    useValidation(value => !commonUtils.isBlank(value) && !isNaN(value.replace(",", ".")), [positionX], validationOptions);
    useValidation(value => !commonUtils.isBlank(value) && !isNaN(value.replace(",", ".")), [positionY], validationOptions);
    useValidation(value => !commonUtils.isBlank(value) && !isNaN(value.replace(",", ".")), [height], validationOptions);
    useValidation(value => !commonUtils.isBlank(value) && !isNaN(value.replace(",", ".")), [direction], validationOptions);
    useValidation(value => !commonUtils.isBlank(value) && !isNaN(value.replace(",", ".")), [mechDowntilt], validationOptions);

    const radios = [...sector.radios].sort(antennaUtils.compareColumns);

    // the sector itself too: saving replaces it, and the buttons below must act on the current one
    const rerenderWhenChanged = [
        sector,
        expanded,
        enabled.value,
        operator.value,
        antennaManufacturer.value,
        antennaName.value,
        positionX.value,
        positionY.value,
        height.value,
        direction.value,
        mechDowntilt.value,
        isAntennaModelOutDated,
        minMaxDowntilt,
        JSON.stringify(sector.radios),
    ];

    const applyAntennaData = async (radio, antennaNameValue) => {
        const antennaData = await antennaRest.getAntennaData(antennaNameValue, null, radio.frequency, null, null);
        radio.antennaFrequency = antennaData.antennaFrequency;
        radio.antennaPort = antennaData.antennaPort;
        radio.antennaElecDowntilt = antennaData.antennaElecDowntilt;
        radio.antennaVirtual = antennaData.antennaVirtual;
    };

    // the antenna model is a sector property, so a change of it affects the port data of every radio of the sector
    const manageAntenna = async (splitName, value) => {
        if (splitName === "antennaManufacturer") {
            antennaName.set(null);
            sector.radios.forEach(radio => {
                radio.antennaFrequency = null;
                radio.antennaPort = null;
                radio.antennaElecDowntilt = null;
                radio.antennaVirtual = false;
            });
        } else if (splitName === "antennaName") {
            for (const radio of sector.radios) {
                await applyAntennaData(radio, value);
            }
        }
    };

    useOnChange(async (name, value) => {
        const [splitName] = name.split("-");
        if (splitName === "enabled") {
            sector.radios.forEach(radio => (radio.enabled = value));
        } else {
            sector[splitName] = value;
            await manageAntenna(splitName, value);
        }
        entryContext.setEntry(entry);
    }, [enabled, operator, antennaManufacturer, antennaName, positionX, positionY, height,
        direction, mechDowntilt]);

    // the buttons live in the memoized markup, so they read the current entry instead of the rendered one
    const onDelete = () => {
        const entry = entryContext.getEntry();
        entry.sectors.splice(entry.sectors.indexOf(sector), 1);
        entryContext.setEntry(entry);
    };

    const onDuplicate = () => {
        const entry = entryContext.getEntry();
        const duplicate = structuredClone(sector);
        delete duplicate.id;
        delete duplicate.key;
        const lastColumn = antennaUtils.lastColumn(sector.radios);
        antennaUtils.shiftColumns(entry, lastColumn, duplicate.radios.length);
        [...duplicate.radios]
            .sort(antennaUtils.compareColumns)
            .forEach((radio, index) => {
                delete radio.id;
                delete radio.key;
                radio.column = lastColumn + 1 + index;
            });
        entry.sectors.splice(entry.sectors.indexOf(sector) + 1, 0, duplicate);
        entryContext.setEntry(entry);
    };

    const onAddRadio = () => {
        const entry = entryContext.getEntry();
        sector.radios.push({ enabled: true, column: antennaUtils.nextColumn(entry) });
        props.onChangeExpanded(true);
        entryContext.setEntry(entry);
    };

    const radioCount = () => {
        if (sector.radios.length === 0) {
            return intl.get("entry.radio.noRadios");
        } else if (allEnabled) {
            return sector.radios.length === 1
                ? intl.get("entry.radio.radioCountSingle")
                : intl.get("entry.radio.radioCount", { count: sector.radios.length });
        }
        return intl.get("entry.radio.radioCountEnabled", { enabled: enabledRadios, count: sector.radios.length });
    };

    const header =
        <HeaderStyle>
            <Checkbox {...enabled.bind}
                title={false}
                indeterminate={enabledRadios > 0 && !allEnabled} />
            <SummaryStyle>
                <TitleStyle>
                    {[sector.antennaManufacturer, sector.antennaName].filter(value => !commonUtils.isBlank(value)).join(" ")}
                </TitleStyle>
                <FactsStyle>
                    <span><span>{intl.get("entry.radio.height")}</span>{formatNumber(sector.height)}</span>
                    <span><span>{intl.get("entry.radio.direction")}</span>{formatNumber(sector.direction)}</span>
                    <span><span>{intl.get("entry.radio.mechDowntilt")}</span>{formatNumber(sector.mechDowntilt)}</span>
                    <span><span>{intl.get("entry.radio.positionX")}</span>{formatNumber(sector.positionX)}</span>
                    <span><span>{intl.get("entry.radio.positionY")}</span>{formatNumber(sector.positionY)}</span>
                </FactsStyle>
            </SummaryStyle>
            <CountStyle>
                {[sector.operator ? intl.get("enum.operator." + sector.operator) : null, radioCount()].filter(value => value !== null).join(" · ")}
            </CountStyle>
            <ActionsStyle>
                {isAntennaModelOutDated &&
                    <Tooltip message={`${intl.get("entry.radio.info.newModel")}. '${sector.antennaName}'`}>
                        <Button svgIcon={mdiAntenna} compact disabled />
                    </Tooltip>
                }
                <Button svgIcon={mdiContentDuplicate} onClick={onDuplicate} compact />
                <Button svgIcon={mdiTrashCanOutline} onClick={onDelete} compact />
            </ActionsStyle>
        </HeaderStyle>;

    return useMemo(() =>
        <Accordion
            header={header}
            expanded={expanded}
            onChange={props.onChangeExpanded}
            dragProps={props.dragProps}>
            <BodyStyle>
                <Grid template="minmax(0, 1.2fr) minmax(0, 1.6fr) minmax(0, 1.2fr) repeat(5, minmax(0, 1fr))">
                    <SelectSingle {...antennaManufacturer.bind} title={intl.get("entry.radio.antennaManufacturer")}>
                        {(async () => {
                            const manufacturers = await antennaRest.getManufacturers();
                            return manufacturers.map(manufacturer =>
                                <option key={manufacturer} value={manufacturer}>{manufacturer}</option>
                            );
                        })}
                    </SelectSingle>
                    <Tooltip message={antennaName.value} enabled={!commonUtils.isBlank(antennaName.value)}>
                        <TooltipFieldStyle>
                            <SelectSingle {...antennaName.bind} title={intl.get("entry.radio.antennaName")} editable={true}>
                                {(async () => {
                                    const row = antenna => {
                                        const originType = ["P", "S", "T"].includes(antenna.originType) ? "N" : "F";
                                        return (
                                            <option key={antenna.id} value={antenna.name}>
                                                <AntennaNameRowStyle>
                                                    <div>{antenna.name}</div>
                                                    <div>{originType}</div>
                                                </AntennaNameRowStyle>
                                            </option>
                                        );
                                    };
                                    if (!antennaManufacturer.value) {
                                        return [];
                                    }
                                    const antennas = await antennaRest.getAntennasByManufacturer(antennaManufacturer.value);
                                    return antennas.map(antenna => row(antenna));
                                })}
                            </SelectSingle>
                        </TooltipFieldStyle>
                    </Tooltip>
                    <SelectSingle {...operator.bind}
                        title={intl.get("entry.radio.operatorName")}
                        toLabel={value => (value ? intl.get("enum.operator." + value) : null)}>
                        <option value={null}></option>
                        <option value="TELEFONICA">{intl.get("enum.operator.TELEFONICA")}</option>
                        <option value="TELEKOM">{intl.get("enum.operator.TELEKOM")}</option>
                        <option value="VODAFONE">{intl.get("enum.operator.VODAFONE")}</option>
                        <option value="_1UND1">{intl.get("enum.operator._1UND1")}</option>
                        <option value="TOWERS_AT">{intl.get("enum.operator.TOWERS_AT")}</option>
                    </SelectSingle>
                    <InputNumber {...height.bind} title={intl.get("entry.radio.height")} fractionDigits={2} textAlign="right" />
                    <InputNumber {...direction.bind} title={intl.get("entry.radio.direction")} fractionDigits={2} textAlign="right" />
                    <InputNumber {...mechDowntilt.bind} title={intl.get("entry.radio.mechDowntilt")} fractionDigits={2} textAlign="right" />
                    <InputNumber {...positionX.bind} title={intl.get("entry.radio.positionX")} fractionDigits={2} textAlign="right" />
                    <InputNumber {...positionY.bind} title={intl.get("entry.radio.positionY")} fractionDigits={2} textAlign="right" />
                </Grid>
                <div>
                    <RadiosTableStyle>
                        {radios.length === 0
                            ? <EmptyStyle>{intl.get("entry.radio.noRadios")}</EmptyStyle>
                            : <table>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>{intl.get("entry.radio.systemName")}</th>
                                        <th>{intl.get("entry.radio.frequency")}</th>
                                        <th>{intl.get("entry.radio.antennaFrequency")}</th>
                                        <th>{intl.get("entry.radio.antennaPort")}</th>
                                        <th>{intl.get("entry.radio.antennaElecDowntilt")}</th>
                                        <th>{intl.get("entry.radio.txPower")}</th>
                                        <th>{intl.get("entry.radio.numChannels")}</th>
                                        <th>{intl.get("entry.radio.attenuation")}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {radios.map(radio =>
                                        <RadioItem
                                            key={antennaUtils.itemKey(radio)}
                                            inputKey={sectorKey + "-" + antennaUtils.itemKey(radio)}
                                            sector={sector}
                                            radio={radio}
                                            minMaxDowntilt={minMaxDowntilt} />
                                    )}
                                </tbody>
                            </table>
                        }
                    </RadiosTableStyle>
                    <AddRadioStyle>
                        <Tooltip message={intl.get("entry.radio.addRadio")}>
                            <Button svgIcon={mdiPlus} onClick={onAddRadio} compact />
                        </Tooltip>
                    </AddRadioStyle>
                </div>
            </BodyStyle>
        </Accordion>, rerenderWhenChanged);
};
